import React, { useEffect, useRef } from 'react';

interface Tree {
  x: number;
  height: number;
  width: number;
  colorIndex: number;
  speed: number;
  offset: number;
  leafCount: number;
  shape: 'pine' | 'broadleaf';
}

interface FloatingLeaf {
  x: number;
  y: number;
  size: number;
  speed: number;
  drift: number;
  rotation: number;
  rotationSpeed: number;
  colorIndex: number;
}

interface Firefly {
  baseX: number;
  baseY: number;
  amplitudeX: number;
  amplitudeY: number;
  speed: number;
  offset: number;
  pulseSpeed: number;
  size: number;
}

interface Sunbeam {
  x: number;
  width: number;
  angle: number;
  drift: number;
  speed: number;
}

const TREE_COLORS = [
  'rgba(34, 139, 34, 0.85)',
  'rgba(46, 125, 50, 0.8)',
  'rgba(56, 142, 60, 0.75)',
  'rgba(67, 160, 71, 0.7)',
];

const LEAF_COLORS = [
  'rgba(76, 175, 80, 0.9)',
  'rgba(129, 199, 132, 0.8)',
  'rgba(102, 187, 106, 0.85)',
];

const ForestBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotionQuery =
      typeof window !== 'undefined'
        ? window.matchMedia?.('(prefers-reduced-motion: reduce)')
        : undefined;
    let reducedMotion = reducedMotionQuery?.matches ?? false;

    let trees: Tree[] = [];
    let floatingLeaves: FloatingLeaf[] = [];
    let fireflies: Firefly[] = [];
    let sunbeams: Sunbeam[] = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initScene();
      if (reducedMotion) {
        renderStatic();
      }
    };

    function initScene() {
      const treeCount = 9;
      trees = Array.from({ length: treeCount }, (_, i) => ({
        x: (canvas.width / (treeCount + 1)) * (i + 1) + (Math.random() - 0.5) * 100,
        height: Math.random() * 300 + 200,
        width: Math.random() * 80 + 60,
        colorIndex: Math.floor(Math.random() * TREE_COLORS.length),
        speed: Math.random() * 0.01 + 0.005,
        offset: Math.random() * Math.PI * 2,
        leafCount: Math.floor(Math.random() * 20) + 15,
        shape: Math.random() > 0.4 ? 'pine' : 'broadleaf',
      }));

      floatingLeaves = Array.from({ length: 22 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 8 + 4,
        speed: Math.random() * 0.3 + 0.1,
        drift: Math.random() * 0.02 + 0.01,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.03,
        colorIndex: Math.floor(Math.random() * LEAF_COLORS.length),
      }));

      fireflies = Array.from({ length: 12 }, () => ({
        baseX: Math.random() * canvas.width,
        baseY: canvas.height * 0.3 + Math.random() * canvas.height * 0.55,
        amplitudeX: 30 + Math.random() * 60,
        amplitudeY: 20 + Math.random() * 40,
        speed: 0.4 + Math.random() * 0.6,
        offset: Math.random() * Math.PI * 2,
        pulseSpeed: 1 + Math.random() * 1.5,
        size: 2 + Math.random() * 2,
      }));

      sunbeams = Array.from({ length: 3 }, (_, i) => ({
        x: (canvas.width / 4) * (i + 1) + (Math.random() - 0.5) * 100,
        width: 120 + Math.random() * 80,
        angle: 0.18 + (Math.random() - 0.5) * 0.05,
        drift: 0,
        speed: 0.002 + Math.random() * 0.002,
      }));
    }

    let time = 0;

    function drawGroundSilhouette() {
      const baseY = canvas.height * 0.82;
      const gradient = ctx.createLinearGradient(0, baseY, 0, canvas.height);
      gradient.addColorStop(0, 'rgba(34, 60, 34, 0)');
      gradient.addColorStop(1, 'rgba(20, 45, 20, 0.55)');
      ctx.fillStyle = gradient;

      ctx.beginPath();
      ctx.moveTo(0, canvas.height);
      ctx.lineTo(0, baseY);
      // Soft rolling hills
      for (let x = 0; x <= canvas.width; x += 40) {
        const hill = Math.sin(x * 0.005 + time * 0.05) * 18;
        ctx.lineTo(x, baseY + hill);
      }
      ctx.lineTo(canvas.width, canvas.height);
      ctx.closePath();
      ctx.fill();
    }

    function drawSunbeams() {
      sunbeams.forEach((b) => {
        b.drift = Math.sin(time * b.speed) * 50;
        ctx.save();
        const startX = b.x + b.drift;
        ctx.translate(startX, 0);
        ctx.rotate(b.angle);
        const grad = ctx.createLinearGradient(0, 0, 0, canvas.height * 1.4);
        grad.addColorStop(0, 'rgba(254, 240, 138, 0.18)');
        grad.addColorStop(0.5, 'rgba(254, 249, 195, 0.10)');
        grad.addColorStop(1, 'rgba(254, 240, 138, 0)');
        ctx.fillStyle = grad;
        ctx.fillRect(-b.width / 2, 0, b.width, canvas.height * 1.4);
        ctx.restore();
      });
    }

    function drawPineTree(tree: Tree) {
      const { x, height, width, colorIndex, speed, offset, leafCount } = tree;
      const sway = Math.sin(time * speed + offset) * 8;

      // Trunk
      const trunkWidth = width * 0.15;
      ctx.fillStyle = 'rgba(101, 67, 33, 0.6)';
      ctx.beginPath();
      ctx.moveTo(x - trunkWidth / 2, canvas.height);
      ctx.lineTo(x - trunkWidth / 2 + sway, canvas.height - height * 0.6);
      ctx.lineTo(x + trunkWidth / 2 + sway, canvas.height - height * 0.6);
      ctx.lineTo(x + trunkWidth / 2, canvas.height);
      ctx.closePath();
      ctx.fill();

      // Triangle foliage layers
      const foliageLevels = 3;
      for (let i = 0; i < foliageLevels; i++) {
        const levelHeight = height * 0.3;
        const levelWidth = width * (1 - i * 0.15);
        const yPos = canvas.height - height * 0.6 - i * levelHeight * 0.7;

        ctx.fillStyle = TREE_COLORS[colorIndex];
        ctx.beginPath();
        ctx.moveTo(x + sway, yPos - levelHeight);
        ctx.lineTo(x - levelWidth / 2 + sway * 0.8, yPos);
        ctx.lineTo(x + levelWidth / 2 + sway * 0.8, yPos);
        ctx.closePath();
        ctx.fill();
      }

      // Decorative leaves
      for (let i = 0; i < leafCount; i++) {
        const angle = (Math.PI * 2 * i) / leafCount + time * 0.5;
        const radius = width * 0.4 + Math.sin(time + i) * 10;
        const leafX = x + Math.cos(angle) * radius + sway * 0.9;
        const leafY = canvas.height - height * 0.75 + Math.sin(angle) * (height * 0.15);

        ctx.fillStyle = LEAF_COLORS[i % LEAF_COLORS.length];
        ctx.beginPath();
        ctx.arc(leafX, leafY, 6, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function drawBroadleafTree(tree: Tree) {
      const { x, height, width, colorIndex, speed, offset } = tree;
      const sway = Math.sin(time * speed + offset) * 6;

      // Trunk
      const trunkWidth = width * 0.18;
      const trunkTop = canvas.height - height * 0.5;
      ctx.fillStyle = 'rgba(101, 67, 33, 0.65)';
      ctx.beginPath();
      ctx.moveTo(x - trunkWidth / 2, canvas.height);
      ctx.lineTo(x - trunkWidth / 2 + sway, trunkTop);
      ctx.lineTo(x + trunkWidth / 2 + sway, trunkTop);
      ctx.lineTo(x + trunkWidth / 2, canvas.height);
      ctx.closePath();
      ctx.fill();

      // Rounded canopy: 3 overlapping circles
      const canopyY = trunkTop;
      const canopyR = width * 0.55;
      const positions = [
        { cx: -width * 0.3, cy: 0, r: canopyR * 0.85 },
        { cx: 0, cy: -canopyR * 0.45, r: canopyR },
        { cx: width * 0.3, cy: 0, r: canopyR * 0.85 },
      ];

      ctx.fillStyle = TREE_COLORS[colorIndex];
      positions.forEach((p) => {
        ctx.beginPath();
        ctx.arc(x + p.cx + sway, canopyY + p.cy, p.r, 0, Math.PI * 2);
        ctx.fill();
      });

      // Highlight leaves
      ctx.fillStyle = LEAF_COLORS[colorIndex % LEAF_COLORS.length];
      for (let i = 0; i < 8; i++) {
        const angle = (Math.PI * 2 * i) / 8 + time * 0.3;
        const r = canopyR * 0.75;
        const lx = x + Math.cos(angle) * r + sway;
        const ly = canopyY - canopyR * 0.2 + Math.sin(angle) * r * 0.6;
        ctx.beginPath();
        ctx.arc(lx, ly, 5, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function drawFloatingLeaf(leaf: FloatingLeaf) {
      leaf.y += leaf.speed;
      leaf.x += Math.sin(time * 2 + leaf.y * 0.01) * leaf.drift;
      leaf.rotation += leaf.rotationSpeed;

      if (leaf.y > canvas.height) {
        leaf.y = -20;
        leaf.x = Math.random() * canvas.width;
      }

      ctx.save();
      ctx.translate(leaf.x, leaf.y);
      ctx.rotate(leaf.rotation);
      ctx.fillStyle = LEAF_COLORS[leaf.colorIndex];
      ctx.beginPath();
      ctx.ellipse(0, 0, leaf.size, leaf.size * 0.6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    function drawFireflies() {
      fireflies.forEach((f) => {
        const x = f.baseX + Math.sin(time * f.speed + f.offset) * f.amplitudeX;
        const y = f.baseY + Math.cos(time * f.speed * 0.7 + f.offset) * f.amplitudeY;
        const pulse = 0.55 + Math.sin(time * f.pulseSpeed + f.offset) * 0.4;

        // Soft halo
        const halo = ctx.createRadialGradient(x, y, 0, x, y, f.size * 5);
        halo.addColorStop(0, `rgba(253, 224, 71, ${0.6 * pulse})`);
        halo.addColorStop(1, 'rgba(253, 224, 71, 0)');
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(x, y, f.size * 5, 0, Math.PI * 2);
        ctx.fill();

        // Bright core
        ctx.fillStyle = `rgba(255, 245, 180, ${pulse})`;
        ctx.beginPath();
        ctx.arc(x, y, f.size, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    function drawSky() {
      const sky = ctx.createLinearGradient(0, 0, 0, canvas.height);
      sky.addColorStop(0, 'rgba(135, 206, 235, 1)');
      sky.addColorStop(0.45, 'rgba(173, 216, 180, 1)');
      sky.addColorStop(0.75, 'rgba(76, 140, 70, 1)');
      sky.addColorStop(1, 'rgba(34, 80, 34, 1)');
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    function renderStatic() {
      drawSky();
      drawSunbeams();
      drawGroundSilhouette();
      trees.forEach((t) => (t.shape === 'pine' ? drawPineTree(t) : drawBroadleafTree(t)));
      drawFireflies();
    }

    function animate() {
      if (reducedMotion) return;

      drawSky();
      time += 0.01;

      drawSunbeams();
      drawGroundSilhouette();
      trees.forEach((tree) =>
        tree.shape === 'pine' ? drawPineTree(tree) : drawBroadleafTree(tree)
      );
      floatingLeaves.forEach(drawFloatingLeaf);
      drawFireflies();

      animationFrameId.current = requestAnimationFrame(animate);
    }

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const handleReducedMotionChange = (event: MediaQueryListEvent) => {
      reducedMotion = event.matches;
      cancelAnimationFrame(animationFrameId.current);

      if (reducedMotion) {
        renderStatic();
      } else {
        animate();
      }
    };

    reducedMotionQuery?.addEventListener?.('change', handleReducedMotionChange);

    if (reducedMotion) {
      renderStatic();
    } else {
      animate();
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      reducedMotionQuery?.removeEventListener?.('change', handleReducedMotionChange);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
    />
  );
};

export default ForestBackground;
